import { Spinner } from "@/components/ui/v2/spinner";
import ViewDocument from "@/pages/(home)/(my-space)/documents/_components/ViewDocument";
import {
  DocumentRequestForm,
  DocumentUploadForm,
  Documents,
  MsMyDocumentsPage,
  MsReceivedRequestsPage,
  MsSentRequestsPage,
  MsSignedDocumentsPage,
  MySpaceDocumentRequestView,
} from "@/utils/lazyLoadComponents";
import type { RouteObject } from "react-router-dom";
import { redirect } from "react-router-dom";
import { addSlashes } from "../route.utils";

// ─── Path Segments ────────────────────────────────────────────────────────────
export const MS_DOCUMENTS_DYNAMIC_ID_KEY = "id";

export enum MS_DOCUMENTS_PATH_SEGMENTS {
  ROOT = "documents",
  MY_DOCUMENTS = "my-documents",
  SENT_REQUESTS = "sent-requests",
  RECEIVED_REQUESTS = "recieved-requests",
  SIGNED_DOCUMENTS = "signed-documents",
  REQUEST = "request",
  UPLOAD = "upload",
  DETAILS = `:${MS_DOCUMENTS_DYNAMIC_ID_KEY}`,
  EDIT = "edit",
  VIEW = "view",
}

// ─── Route Definition ─────────────────────────────────────────────────────────
export const mySpaceDocumentsRoutes: RouteObject = {
  path: MS_DOCUMENTS_PATH_SEGMENTS.ROOT,
  handle: { breadcrumb: "Documents" },
  HydrateFallback: Spinner,
  // No Component here — only the 4 tab index pages get the page-level tabs layout
  children: [
    // ── Page-level tabs layout: each tab owns its own stats + table ──────────
    {
      Component: Documents,
      handle: { flushContent: true },
      children: [
        {
          index: true,
          loader: () => redirect(MS_DOCUMENTS_PATHS.MY_DOCUMENTS),
        },
        {
          path: MS_DOCUMENTS_PATH_SEGMENTS.MY_DOCUMENTS,
          handle: { breadcrumb: "My Documents" },
          Component: MsMyDocumentsPage,
        },
        {
          path: MS_DOCUMENTS_PATH_SEGMENTS.SENT_REQUESTS,
          handle: { breadcrumb: "Sent Requests" },
          Component: MsSentRequestsPage,
        },
        {
          path: MS_DOCUMENTS_PATH_SEGMENTS.RECEIVED_REQUESTS,
          handle: { breadcrumb: "Received Requests" },
          Component: MsReceivedRequestsPage,
        },
        {
          path: MS_DOCUMENTS_PATH_SEGMENTS.SIGNED_DOCUMENTS,
          handle: { breadcrumb: "Signed Documents" },
          Component: MsSignedDocumentsPage,
        },
      ],
    },

    // ── My documents detail routes (no stats, no tabs) ──────────────────────
    {
      path: MS_DOCUMENTS_PATH_SEGMENTS.MY_DOCUMENTS,
      handle: { breadcrumb: "My Documents" },
      children: [
        {
          path: MS_DOCUMENTS_PATH_SEGMENTS.DETAILS,
          handle: { breadcrumb: "View Document" },
          Component: ViewDocument,
        },
        {
          // Same form as a fresh upload, in update mode (it reads the `:id`).
          path: `${MS_DOCUMENTS_PATH_SEGMENTS.DETAILS}/${MS_DOCUMENTS_PATH_SEGMENTS.EDIT}`,
          handle: { breadcrumb: "Update Document" },
          Component: DocumentUploadForm,
        },
      ],
    },

    // ── Sent requests detail routes (no stats, no tabs) ──────────────────────
    {
      path: MS_DOCUMENTS_PATH_SEGMENTS.SENT_REQUESTS,
      handle: { breadcrumb: "Sent Requests" },
      children: [
        {
          path: MS_DOCUMENTS_PATH_SEGMENTS.DETAILS,
          handle: { breadcrumb: "Request" },
          Component: MySpaceDocumentRequestView,
        },
        {
          path: `${MS_DOCUMENTS_PATH_SEGMENTS.DETAILS}/${MS_DOCUMENTS_PATH_SEGMENTS.EDIT}`,
          handle: { breadcrumb: "Edit" },
          Component: DocumentRequestForm,
        },
      ],
    },

    // ── Received requests detail routes (no stats, no tabs) ──────────────────
    {
      path: MS_DOCUMENTS_PATH_SEGMENTS.RECEIVED_REQUESTS,
      handle: { breadcrumb: "Received Requests" },
      children: [
        {
          path: MS_DOCUMENTS_PATH_SEGMENTS.DETAILS,
          handle: { breadcrumb: "Request" },
          Component: MySpaceDocumentRequestView,
        },
      ],
    },

    // ── Request + upload (no stats, no tabs) ─────────────────────────────────
    {
      path: MS_DOCUMENTS_PATH_SEGMENTS.REQUEST,
      handle: { breadcrumb: "Document Request" },
      children: [
        { index: true, Component: DocumentRequestForm },
        {
          path: MS_DOCUMENTS_PATH_SEGMENTS.UPLOAD,
          handle: { breadcrumb: "Upload" },
          Component: DocumentUploadForm,
        },
      ],
    },
  ],
};

// ─── Resolved Paths ───────────────────────────────────────────────────────────
export const MS_DOCUMENTS_PATHS = Object.freeze({
  ROOT: addSlashes(MS_DOCUMENTS_PATH_SEGMENTS.ROOT),
  MY_DOCUMENTS: addSlashes(
    MS_DOCUMENTS_PATH_SEGMENTS.ROOT,
    MS_DOCUMENTS_PATH_SEGMENTS.MY_DOCUMENTS,
  ),
  SENT_REQUESTS: addSlashes(
    MS_DOCUMENTS_PATH_SEGMENTS.ROOT,
    MS_DOCUMENTS_PATH_SEGMENTS.SENT_REQUESTS,
  ),
  RECEIVED_REQUESTS: addSlashes(
    MS_DOCUMENTS_PATH_SEGMENTS.ROOT,
    MS_DOCUMENTS_PATH_SEGMENTS.RECEIVED_REQUESTS,
  ),
  SIGNED_DOCUMENTS: addSlashes(
    MS_DOCUMENTS_PATH_SEGMENTS.ROOT,
    MS_DOCUMENTS_PATH_SEGMENTS.SIGNED_DOCUMENTS,
  ),
  VIEW: (id: string | number) =>
    addSlashes(
      MS_DOCUMENTS_PATH_SEGMENTS.ROOT,
      MS_DOCUMENTS_PATH_SEGMENTS.MY_DOCUMENTS,
      String(id),
    ),
  EDIT: (id: string | number) =>
    addSlashes(
      MS_DOCUMENTS_PATH_SEGMENTS.ROOT,
      MS_DOCUMENTS_PATH_SEGMENTS.MY_DOCUMENTS,
      String(id),
      MS_DOCUMENTS_PATH_SEGMENTS.EDIT,
    ),
  REQUEST: addSlashes(
    MS_DOCUMENTS_PATH_SEGMENTS.ROOT,
    MS_DOCUMENTS_PATH_SEGMENTS.REQUEST,
  ),
  UPLOAD: addSlashes(
    MS_DOCUMENTS_PATH_SEGMENTS.ROOT,
    MS_DOCUMENTS_PATH_SEGMENTS.REQUEST,
    MS_DOCUMENTS_PATH_SEGMENTS.UPLOAD,
  ),
  SENT_REQUEST_VIEW: (id: string | number) =>
    addSlashes(
      MS_DOCUMENTS_PATH_SEGMENTS.ROOT,
      MS_DOCUMENTS_PATH_SEGMENTS.SENT_REQUESTS,
      String(id),
    ),
  SENT_REQUEST_EDIT: (id: string | number) =>
    addSlashes(
      MS_DOCUMENTS_PATH_SEGMENTS.ROOT,
      MS_DOCUMENTS_PATH_SEGMENTS.SENT_REQUESTS,
      String(id),
      MS_DOCUMENTS_PATH_SEGMENTS.EDIT,
    ),
  RECEIVED_REQUEST_VIEW: (id: string | number) =>
    addSlashes(
      MS_DOCUMENTS_PATH_SEGMENTS.ROOT,
      MS_DOCUMENTS_PATH_SEGMENTS.RECEIVED_REQUESTS,
      String(id),
    ),
});
